// ============================================
// Path: /frontend/src/components/layout/Header.jsx
// Purpose: Main navigation header with auth state and mobile menu
// ============================================

import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { Menu, User, LogOut, List, ChevronDown, Inbox, Send, LayoutDashboard, Handshake, FileText, MessageCircle, Share2, BarChart3, DollarSign, Bookmark, GitCompare, Bell, Users } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

export function Header() {
  const { user, loading, logout } = useAuth();

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        <div className="mr-4 hidden md:flex">
          <Link to="/" className="mr-6 flex items-center space-x-2">
            <img src="/images/logo.png" alt="MotoHome Finder" className="h-7 w-7 sm:h-8 sm:w-8" />
            <span className="hidden font-bold sm:inline-block">MotoHome Finder</span>
          </Link>
          <nav className="flex items-center space-x-6 text-sm font-medium">
            <Link
              to="/vehicles"
              className="transition-colors hover:text-foreground/80 text-foreground/60"
            >
              Vehicles
            </Link>
            <Link
              to="/properties"
              className="transition-colors hover:text-foreground/80 text-foreground/60"
            >
              Properties
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 transition-colors hover:text-foreground/80 text-foreground/60">
                Dealers & Agents
                <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem asChild>
                  <Link to="/car-dealers">Car Dealers</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/real-estate-agents">Real Estate Agents</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>

        {/* Mobile Menu */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Toggle Menu">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[300px] sm:w-[400px]">
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
                <Link to="/" className="flex items-center space-x-2">
                  <img src="/images/logo.png" alt="MotoHome Finder" className="h-7 w-7 sm:h-8 sm:w-8" />
                  <span className="font-bold text-lg">MotoHome Finder</span>
                </Link>
              </div>
              <nav className="flex flex-col gap-1 flex-1 overflow-y-auto">
                <Link
                  to="/vehicles"
                  className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                >
                  Vehicles
                </Link>
                <Link
                  to="/properties"
                  className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                >
                  Properties
                </Link>
                <Link
                  to="/car-dealers"
                  className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                >
                  Car Dealers
                </Link>
                <Link
                  to="/real-estate-agents"
                  className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                >
                  Real Estate Agents
                </Link>

                 <Link
                  to="/finance-application"
                  className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md flex items-center gap-2"
                >
                  
                  Finance ?
                </Link>

                {!loading && user?.role === 'ADMIN' && (
                  <Link
                    to="/admin"
                    className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                  >
                    Admin
                  </Link>
                )}

                {!loading && user && (
                  <>
                    <div className="h-px bg-border my-2"></div>
                    <Link
                      to="/dashboard"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      Dashboard
                    </Link>

                    <Link
                      to="/my-listings"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      My Listings
                    </Link>
                    <Link
                      to="/my-inquiries"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      My Inquiries
                    </Link>
                    <Link
                      to="/received-inquiries"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      Received Inquiries
                    </Link>
                    <Link
                      to="/my-connections"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      My Connections
                    </Link>
                    <Link
                      to="/joint-ventures"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      Joint Ventures
                    </Link>

                    <Link
                      to="/dashboard/whatsapp-tools"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      WhatsApp Tools
                    </Link>
                    <Link
                      to="/dashboard/whatsapp-analytics"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      WhatsApp Analytics
                    </Link>

                    <Link
                      to="/dashboard/facebook-tools"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      Facebook Tools
                    </Link>
                    <Link
                      to="/dashboard/facebook-analytics"
                      className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                    >
                      Facebook Analytics
                    </Link>

                    <Link
                    to="/saved-listings"
                    className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                  >
                    Saved Listings
                  </Link>
                  
                  <Link
                    to="/compare"
                    className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                  >
                    Compare Listings
                  </Link>
                  
                  <Link
                    to="/my-price-alerts"
                    className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                  >
                    Price Alerts
                  </Link>
                  
                  <Link
                    to="/my-chama-interests"
                    className="text-sm sm:text-base font-medium transition-all duration-200 hover:bg-accent hover:text-accent-foreground text-foreground px-3 py-2.5 rounded-md"
                  >
                    My Shares Interests
                  </Link>
                  </>
                )}


              </nav>
              <div className="mt-auto pt-4 border-t border-border flex flex-col gap-2.5 bg-background">
                {!loading && user ? (
                  <Button variant="outline" onClick={logout} className="text-xs sm:text-sm h-10 sm:h-11 font-medium">
                    Logout
                  </Button>
                ) : !loading ? (
                  <>
                    <Button variant="outline" asChild className="text-xs sm:text-sm h-10 sm:h-11 font-medium">
                      <Link to="/login">Login</Link>
                    </Button>
                    <Button asChild className="text-xs sm:text-sm h-10 sm:h-11 font-medium">
                      <Link to="/register">Sign Up</Link>
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
          </SheetContent>
        </Sheet>

        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <Button variant="outline" asChild className="text-xs sm:text-sm h-9 sm:h-10">
            <Link to="/listings/create">Create Listing</Link>
          </Button>

          <Button variant="outline" asChild className="hidden md:flex text-xs sm:text-sm h-9 sm:h-10">
            <Link to="/finance-application" className="flex items-center gap-2">
              Finance ?
            </Link>
          </Button>

          <div className="hidden md:flex md:items-center md:gap-2">
            {!loading && user ? (
              <>
                {/* Joint Venture Dropdown - Only for Logged In Users */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Handshake className="mr-2 h-4 w-4" />
                      Joint Ventures
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/joint-ventures">
                        <Handshake className="mr-2 h-4 w-4" />
                        <span>Submit Interest</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/jv/my-submission">
                        <FileText className="mr-2 h-4 w-4" />
                        <span>My Submission</span>
                      </Link>
                    </DropdownMenuItem>
                    {user.role === 'ADMIN' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem asChild>
                          <Link to="/admin">
                            <LayoutDashboard className="mr-2 h-4 w-4" />
                            <span>Admin Dashboard</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/submissions">
                            <FileText className="mr-2 h-4 w-4" />
                            <span>All Submissions</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/matches">
                            <Handshake className="mr-2 h-4 w-4" />
                            <span>Matches (90%+)</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/connections">
                            <User className="mr-2 h-4 w-4" />
                            <span>Connections</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/reports">
                            <FileText className="mr-2 h-4 w-4" />
                            <span>Reports</span>
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* User Account Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary">
                      My Account
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>
                      <p>My Account</p>
                      <p className="text-xs text-muted-foreground font-normal">{user.email}</p>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        <span>Dashboard</span>
                      </Link>
                    </DropdownMenuItem>

                    {user.role === 'ADMIN' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin">
                          <User className="mr-2 h-4 w-4" />
                          <span>Admin Dashboard</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/my-listings">
                        <List className="mr-2 h-4 w-4" />
                        <span>My Listings</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-inquiries">
                        <Send className="mr-2 h-4 w-4" />
                        <span>My Inquiries</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                      <Link to="/received-inquiries">
                        <Inbox className="mr-2 h-4 w-4" />
                        <span>Received Inquiries</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-connections">
                        <MessageCircle className="mr-2 h-4 w-4" />
                        <span>My Connections</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuItem asChild>
                    <Link to="/dashboard/whatsapp-tools">
                      <Share2 className="mr-2 h-4 w-4" />
                      <span>WhatsApp Tools</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/whatsapp-analytics">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      <span>WhatsApp Analytics</span>
                    </Link>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild>
                  <Link to="/dashboard/facebook-tools">
                    <Share2 className="mr-2 h-4 w-4" />
                    <span>Facebook Tools</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/facebook-analytics">
                    <BarChart3 className="mr-2 h-4 w-4" />
                    <span>Facebook Analytics</span>
                  </Link>
                </DropdownMenuItem>

                <DropdownMenuItem asChild>
                      <Link to="/saved-listings">
                        <Bookmark className="mr-2 h-4 w-4" />
                        <span>Saved Listings</span>
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem asChild>
                      <Link to="/compare">
                        <GitCompare className="mr-2 h-4 w-4" />
                        <span>Compare Listings</span>
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem asChild>
                      <Link to="/my-price-alerts">
                        <Bell className="mr-2 h-4 w-4" />
                        <span>Price Alerts</span>
                      </Link>
                    </DropdownMenuItem>
                    
                    <DropdownMenuItem asChild>
                      <Link to="/my-chama-interests">
                        <Users className="mr-2 h-4 w-4" />
                        <span>My Shares Interests</span>
                      </Link>
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Logout</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : !loading ? (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login">Login</Link>
                </Button>
                <Button asChild>
                  <Link to="/register">Sign Up</Link>
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}